import { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format, differenceInDays } from 'date-fns';
import { sv } from 'date-fns/locale';
import { ChevronLeft, User, Users, MessageSquare, AlertTriangle, Check } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { BookingTimeline } from '@/components/booking/BookingTimeline';
import { mapBookingToTimeline } from '@/lib/booking/timeline';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  parsePriceTextToOre,
  getStayPrice,
  formatOreToKr,
  type PriceRule,
} from '@/lib/accommodation/pricing';
import { computePrepaymentAmountOre, resolvePrepaymentType } from '@/lib/booking/prepayment';
import { useBookingPayments } from '@/hooks/useBookingPayments';
import { BookingPaymentSection } from '@/components/booking/BookingPaymentSection';
import { getBookingListStatus } from '@/lib/booking/listStatus';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';


export default function AccommodationBookingDetailPage() {
  const { farmId, bookingId } = useParams<{ farmId: string; bookingId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State for confirm dialog
  const [requiresPrepayment, setRequiresPrepayment] = useState(false);
  const [prepaymentAmountKr, setPrepaymentAmountKr] = useState('');
  const [acceptDefaultsLoaded, setAcceptDefaultsLoaded] = useState(false);

  // Booking payments hook
  const {
    payments, totalPaidOre, isLoading: paymentsLoading,
    addPayment, deletePayment,
  } = useBookingPayments(bookingId);

  const { data: booking, isLoading } = useQuery({
    queryKey: ['accommodation-booking-detail', bookingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accommodation_bookings')
        .select(`
          id, accommodation_id, guest_name, guest_email, guest_phone, guest_message,
          status, guests, check_in, check_out, booking_code, created_at, cancelled_at,
          requires_prepayment, prepayment_amount_ore, payment_status, payment_due_at, paid_at, amount_paid_ore,
          producer_accommodations!accommodation_bookings_accommodation_id_fkey (
            id, title, price_per_night_text, prepayment_policy_default, prepayment_amount_default_ore, prepayment_type, prepayment_percentage
          )
        `)
        .eq('id', bookingId!)
        .eq('producer_id', farmId!)
        .single();
      if (error) throw error;
      return data as any;
    },
    enabled: !!bookingId && !!farmId,
  });

  const accommodationId = booking?.accommodation_id;

  const { data: priceRules = [] } = useQuery({
    queryKey: ['booking-detail-price-rules', accommodationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accommodation_price_rules')
        .select('id, rule_type, title, start_date, end_date, price_ore, is_active')
        .eq('accommodation_id', accommodationId!)
        .eq('is_active', true);
      if (error) throw error;
      return (data ?? []) as PriceRule[];
    },
    enabled: !!accommodationId,
  });

  // Compute stay total for percentage-based prepayment
  const accForPrice = booking?.producer_accommodations as any;
  const basePriceOreForPrefill = accForPrice?.price_per_night_text ? parsePriceTextToOre(accForPrice.price_per_night_text) : null;
  const stayPriceForPrefill = useMemo(() => {
    if (!booking || !basePriceOreForPrefill) return null;
    const nights = differenceInDays(new Date(booking.check_out), new Date(booking.check_in));
    if (nights <= 0) return null;
    return getStayPrice(booking.check_in, booking.check_out, basePriceOreForPrefill, priceRules);
  }, [booking, basePriceOreForPrefill, priceRules]);

  // Prefill accept dialog from accommodation defaults
  useEffect(() => {
    if (!booking || acceptDefaultsLoaded || booking.status !== 'pending') return;
    const acc = booking.producer_accommodations as any;
    if (!acc) return;
    const policy = acc.prepayment_policy_default || 'none';
    const prepType = resolvePrepaymentType(acc.prepayment_type);

    const computedOre = computePrepaymentAmountOre(
      prepType,
      acc.prepayment_amount_default_ore,
      acc.prepayment_percentage,
      stayPriceForPrefill?.total_ore,
    );
    const computedKr = computedOre ? Math.round(computedOre / 100).toString() : '';

    if (policy === 'required') {
      setRequiresPrepayment(true);
      if (computedKr) setPrepaymentAmountKr(computedKr);
    } else if (policy === 'optional') {
      if (computedKr) setPrepaymentAmountKr(computedKr);
    }
    // Only mark loaded once we have price data (for percentage) or type is fixed
    if (prepType === 'fixed' || stayPriceForPrefill) {
      setAcceptDefaultsLoaded(true);
    }
  }, [booking, acceptDefaultsLoaded, stayPriceForPrefill]);

  const invalidateBooking = () => {
    queryClient.invalidateQueries({ queryKey: ['accommodation-booking-detail', bookingId] });
    queryClient.invalidateQueries({ queryKey: ['producer-accommodation-bookings-hub'] });
  };

  // Confirm booking (direct)
  const confirmMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('accommodation_bookings')
        .update({
          status: 'confirmed',
          payment_status: 'none',
          requires_prepayment: false,
          updated_at: new Date().toISOString(),
        } as any)
        .eq('id', bookingId!)
        .eq('producer_id', farmId!);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Bokningen är nu bekräftad');
      invalidateBooking();
    },
    onError: () => toast.error('Kunde inte bekräfta bokningen'),
  });

  // Reserve pending payment
  const reserveMutation = useMutation({
    mutationFn: async ({ amountOre }: { amountOre: number }) => {
      const { error } = await supabase
        .from('accommodation_bookings')
        .update({
          status: 'reserved_pending_payment',
          requires_prepayment: true,
          prepayment_amount_ore: amountOre,
          payment_status: 'pending',
          updated_at: new Date().toISOString(),
        } as any)
        .eq('id', bookingId!)
        .eq('producer_id', farmId!);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Bokningen är reserverad i väntan på betalning');
      invalidateBooking();
    },
    onError: () => toast.error('Kunde inte reservera bokningen'),
  });


  // Cancel
  const cancelMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('accommodation_bookings')
        .update({ status: 'cancelled', cancelled_at: new Date().toISOString() } as any)
        .eq('id', bookingId!)
        .eq('producer_id', farmId!);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Bokningen har avbokats');
      invalidateBooking();
    },
    onError: () => toast.error('Kunde inte avboka'),
  });

  if (isLoading) {
    return <div className="py-12 text-center text-muted-foreground text-sm">Laddar bokning…</div>;
  }

  if (!booking) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Bokningen hittades inte</p>
        <Button variant="outline" size="sm" className="mt-4" asChild>
          <Link to={`/app/producer/farm/${farmId}/bookings?type=accommodation`}>Tillbaka</Link>
        </Button>
      </div>
    );
  }

  const acc = booking.producer_accommodations;
  const checkIn = new Date(booking.check_in);
  const checkOut = new Date(booking.check_out);
  const nights = differenceInDays(checkOut, checkIn);
  const isCancelled = booking.status === 'cancelled';
  const isPending = booking.status === 'pending';
  const isReserved = booking.status === 'reserved_pending_payment';
  const paymentStatus = booking.payment_status ?? 'none';

  // Price calculation
  const basePriceOre = acc?.price_per_night_text ? parsePriceTextToOre(acc.price_per_night_text) : null;
  const stayPrice = basePriceOre && nights > 0
    ? getStayPrice(booking.check_in, booking.check_out, basePriceOre, priceRules)
    : null;

  const avgNightKr = stayPrice ? Math.round(stayPrice.total_ore / nights / 100) : (basePriceOre ? Math.round(basePriceOre / 100) : null);
  const totalKr = stayPrice ? Math.round(stayPrice.total_ore / 100) : null;

  // Payment-aware display state for header badge
  const displayState = getBookingListStatus({
    type: 'accommodation',
    status: booking.status,
    paymentStatus,
    amountPaidOre: totalPaidOre,
    totalStayOre: stayPrice?.total_ore ?? null,
    requiresPrepayment: booking.requires_prepayment,
    prepaymentAmountOre: booking.prepayment_amount_ore,
  });

  const toneToClassName: Record<string, string> = {
    attention: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300',
    progress: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300',
    success: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300',
    neutral: 'bg-muted text-muted-foreground border-border',
    destructive: 'bg-destructive/10 text-destructive border-destructive/20',
  };

  const handleConfirm = () => {
    if (requiresPrepayment) {
      const amountKr = parseInt(prepaymentAmountKr, 10);
      if (!amountKr || amountKr <= 0) {
        toast.error('Ange ett giltigt belopp för förskottsbetalning');
        return;
      }
      reserveMutation.mutate({ amountOre: amountKr * 100 });
    } else {
      confirmMutation.mutate();
    }
  };

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to={`/app/producer/farm/${farmId}/bookings?type=accommodation`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        Tillbaka till bokningar
      </Link>

      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-foreground">{acc?.title ?? 'Boende'}</h1>
            <p className="text-lg text-foreground/80">
              {format(checkIn, 'd MMM', { locale: sv })} – {format(checkOut, 'd MMM yyyy', { locale: sv })}
              <span className="text-muted-foreground ml-2 text-base">
                · {nights} {nights === 1 ? 'natt' : 'nätter'}
              </span>
            </p>
          </div>
          <Badge variant="outline" className={cn('text-sm shrink-0 mt-1', toneToClassName[displayState.tone] ?? '')}>
            {displayState.label}
          </Badge>
        </div>
        {booking.booking_code && (
          <p className="text-xs text-muted-foreground">Bokningskod: {booking.booking_code}</p>
        )}
      </div>

      <Separator />

      {/* Booking Timeline */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Status</h2>
        <Card>
          <CardContent className="py-5 px-5">
            <BookingTimeline
              steps={mapBookingToTimeline({
                bookingStatus: booking.status,
                paymentStatus,
                requiresPrepayment: booking.requires_prepayment ?? false,
                amountPaidOre: totalPaidOre,
                totalStayOre: stayPrice?.total_ore ?? null,
                createdAt: booking.created_at,
                checkIn: booking.check_in,
                checkOut: booking.check_out,
                cancelledAt: booking.cancelled_at,
                paidAt: booking.paid_at,
              })}
            />
          </CardContent>
        </Card>
      </section>

      {/* Host accept actions for pending bookings */}
      {isPending && (
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Hantera förfrågan</h2>
          <Card>
            <CardContent className="py-5 px-5 space-y-5">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="prepayment-toggle" className="text-sm font-medium text-foreground">
                    Kräv förskottsbetalning
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Gästen behöver betala innan bokningen säkras
                  </p>
                </div>
                <Switch
                  id="prepayment-toggle"
                  checked={requiresPrepayment}
                  onCheckedChange={setRequiresPrepayment}
                />
              </div>

              {requiresPrepayment && (
                <div className="space-y-2 pl-1">
                  <Label htmlFor="prepayment-amount" className="text-sm text-foreground">
                    Belopp att betala (kr)
                  </Label>
                  <Input
                    id="prepayment-amount"
                    type="number"
                    min={1}
                    placeholder={totalKr ? `t.ex. ${totalKr}` : 'Belopp i kr'}
                    value={prepaymentAmountKr}
                    onChange={(e) => setPrepaymentAmountKr(e.target.value)}
                    className="max-w-[200px]"
                  />
                  {totalKr && (
                    <p className="text-xs text-muted-foreground">
                      Totalt vistelsebelopp: {totalKr.toLocaleString('sv-SE')} kr
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button className="gap-2">
                      <Check className="h-4 w-4" />
                      {requiresPrepayment ? 'Reservera i väntan på betalning' : 'Bekräfta bokning'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {requiresPrepayment ? 'Reservera bokning?' : 'Bekräfta bokning?'}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {requiresPrepayment
                          ? `Bokningen reserveras och gästen behöver betala ${prepaymentAmountKr ? `${parseInt(prepaymentAmountKr).toLocaleString('sv-SE')} kr` : 'angivet belopp'} innan den blir fullt bekräftad.`
                          : 'Bokningen bekräftas direkt. Gästen meddelas om bekräftelsen.'
                        }
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Avbryt</AlertDialogCancel>
                      <AlertDialogAction onClick={handleConfirm}>
                        {confirmMutation.isPending || reserveMutation.isPending ? 'Sparar…' : 'Ja, fortsätt'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Payment section */}
      <BookingPaymentSection
        payments={payments}
        totalPaidOre={totalPaidOre}
        totalStayOre={stayPrice?.total_ore ?? null}
        prepaymentAmountOre={booking.prepayment_amount_ore}
        requiresPrepayment={booking.requires_prepayment ?? false}
        isReserved={isReserved}
        isCancelled={isCancelled}
        isPending={isPending}
        onAddPayment={async (amountOre, paidAt, note) => {
          await addPayment.mutateAsync({ producerId: farmId!, amountOre, paidAt, note: note ?? undefined });
        }}
        onDeletePayment={async (paymentId) => {
          await deletePayment.mutateAsync({ paymentId, producerId: farmId! });
        }}
        isAdding={addPayment.isPending}
        isDeleting={deletePayment.isPending}
        legacyAmountPaidOre={booking.amount_paid_ore ?? 0}
        legacyPaidAt={booking.paid_at}
      />

      {/* Guest */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Gäst</h2>
        <Card>
          <CardContent className="py-4 px-4 space-y-3">
            {booking.guest_name && (
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium text-foreground">{booking.guest_name}</span>
              </div>
            )}
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{booking.guest_email}</span>
            </div>
            {booking.guest_phone && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">{booking.guest_phone}</span>
              </div>
            )}
            {booking.guests > 1 && (
              <div className="flex items-center gap-3">
                <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm text-muted-foreground">{booking.guests} gäster</span>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Booking details */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Bokningsdetaljer</h2>
        <Card>
          <CardContent className="py-4 px-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs mb-0.5">Incheckning</p>
                <p className="font-medium text-foreground">{format(checkIn, 'd MMMM yyyy', { locale: sv })}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-0.5">Utcheckning</p>
                <p className="font-medium text-foreground">{format(checkOut, 'd MMMM yyyy', { locale: sv })}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-0.5">Boende</p>
                <p className="font-medium text-foreground">{acc?.title ?? '–'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-0.5">Bokad</p>
                <p className="font-medium text-foreground">{format(new Date(booking.created_at), 'd MMM yyyy', { locale: sv })}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Price */}
      {(avgNightKr || totalKr) && (
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Pris</h2>
          <Card>
            <CardContent className="py-4 px-4 space-y-2">
              {avgNightKr && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {nights} {nights === 1 ? 'natt' : 'nätter'} × {avgNightKr.toLocaleString('sv-SE')} kr
                  </span>
                  {totalKr && (
                    <span className="text-foreground">{totalKr.toLocaleString('sv-SE')} kr</span>
                  )}
                </div>
              )}
              {stayPrice?.has_mixed_pricing && (
                <p className="text-xs text-muted-foreground">Priset varierar under vistelsen</p>
              )}
              {totalKr && (
                <>
                  <Separator />
                  <div className="flex justify-between text-sm font-medium">
                    <span className="text-foreground">Totalt</span>
                    <span className="text-foreground">{totalKr.toLocaleString('sv-SE')} kr</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </section>
      )}

      {/* Guest message */}
      {booking.guest_message && (
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Meddelande från gäst</h2>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex gap-3">
                <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-sm text-foreground/80 whitespace-pre-wrap">{booking.guest_message}</p>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Actions */}
      {!isCancelled && (
        <section className="space-y-3 pb-8">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Åtgärder</h2>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/5">
                <AlertTriangle className="h-4 w-4 mr-2" />
                Avboka
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Avboka bokning?</AlertDialogTitle>
                <AlertDialogDescription>
                  Gästen kommer att meddelas om avbokningen. Denna åtgärd kan inte ångras.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => cancelMutation.mutate()}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {cancelMutation.isPending ? 'Avbokar…' : 'Ja, avboka'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      )}

      {isCancelled && booking.cancelled_at && (
        <p className="text-xs text-muted-foreground pb-8">
          Avbokad {format(new Date(booking.cancelled_at), 'd MMM yyyy', { locale: sv })}
        </p>
      )}
    </div>
  );
}
